<template>
  <div class="set-new-password-form">
    <template v-if="isSuccess & !isError">
      <NotificationBanner
        :message="$t('password-reset.password-update-succeeded-message')"
        is-success
      />

      <RouterLinkButton :to="{ path: '/login' }">
        {{ $t('general.login') }}
      </RouterLinkButton>
    </template>

    <template v-else>
      <NotificationBanner
        v-if="isError"
        :message="$t('password-reset.password-update-failed-message')"
        is-danger
      />

      <form autocomplete="off" spellcheck="false" @submit.prevent="submit">
        <NotificationBanner
          v-if="!formError.isOk && formError.message"
          :message="formError.message"
          is-danger
        />

        <FormTextInput
          v-model="formData.password"
          name="password"
          type="password"
          :label="$t('field-labels.new-password')"
          :is-disabled="isLoading"
          is-required
        />

        <FormTextInput
          v-model="formData.confirmation"
          name="confirmation"
          type="password"
          :label="$t('field-labels.password-confirmation')"
          :is-disabled="isLoading"
          is-required
        />

        <FormButton :is-busy="isLoading" :is-disabled="!formError.isOk">
          {{ $t('password-reset.update-password') }}
        </FormButton>
      </form>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { PropType, computed, ref } from 'vue'

import FormButton from '@/components/common/FormButton.vue'
import FormTextInput from '@/components/common/FormTextInput.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import RouterLinkButton from '@/components/common/RouterLinkButton.vue'
import i18n from '@/i18n'
import PasswordReset from '@/utils/password-reset'

const props = defineProps({
  token: {
    type: String as PropType<string>,
    required: true,
  },
})

/* local state */

const formData = ref<{ password: string; confirmation: string }>({
  password: '',
  confirmation: '',
})
const isError = ref<boolean>(false)
const isLoading = ref<boolean>(false)
const isSuccess = ref<boolean>(false)

/* computed */

interface FormError {
  isOk: boolean
  message?: string
}

const formError = computed<FormError>((): FormError => {
  const password = formData.value.password as string
  const confirmation = formData.value.confirmation as string

  if (password.length === 0 && confirmation.length === 0) {
    return { isOk: false }
  }

  if (password.length < 8) {
    return {
      isOk: false,
      message: i18n.global.t('password-reset.password-too-short-error'),
    }
  }

  if (password.length > 72) {
    return {
      isOk: false,
      message: i18n.global.t('password-reset.password-too-long-error'),
    }
  }

  if (confirmation !== password) {
    return {
      isOk: false,
      message: i18n.global.t('password-reset.password-mismatch'),
    }
  }

  return { isOk: true }
})

/* actions */

const submit = async (): Promise<void> => {
  isLoading.value = true

  const isPasswordUpdated = await PasswordReset.updatePassword(
    props.token,
    formData.value.password,
  )

  isSuccess.value = isPasswordUpdated
  isError.value = !isPasswordUpdated
  formData.value = { password: '', confirmation: '' }
  isLoading.value = false
}
</script>

<style lang="scss" scoped>
@import 'src/assets/scss/globals';

.set-new-password-form,
.set-new-password-form > form {
  display: flex;
  flex-flow: column nowrap;
  gap: 1em;
}
</style>
